import { FilesetResolver, FaceLandmarker } from "@mediapipe/tasks-vision";
import { isMobileDevice } from "../utils/camera.util"; // Updated import to use camera.util instead of deviceDetection.util
import { safeConsole } from "../utils/logger";

class FaceDetectionService {
  constructor() {
    this.faceLandmarker = null;
    this.isInitialized = false;
    this.isInitializing = false;
    this.initializationPromise = null;
    this.performanceMetrics = {
      lastDetectionTime: 0,
      averageDetectionTime: 0,
      frameCount: 0,
    };
  }

  /**
   * Initialize MediaPipe Face Landmarker
   * Uses Face Landmarker model with refined landmarks for iris tracking
   */
  async initialize() {
    if (this.isInitialized) return this.faceLandmarker;
    if (this.isInitializing) return this.initializationPromise;

    this.isInitializing = true;
    this.initializationPromise = this._doInitialize();

    try {
      const result = await this.initializationPromise;
      return result;
    } finally {
      this.isInitializing = false;
    }
  }

  async _doInitialize() {
    try {
      // MediaPipe WASM URL (Updated to CRA env var if needed, or fallback to default CDN if not set)
      // Usually MediaPipe manages this internally if not specified, or use public folder path
      // IMPORTANT: Adjust these paths to where you serve MediaPipe assets or use a CDN
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm",
      );

      this.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numFaces: 1,
        minFaceDetectionConfidence: 0.5,
        minFacePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
      });

      this.isInitialized = true;
      safeConsole.log(`✅ Face Detection Service initialized`);
      return this.faceLandmarker;
    } catch (error) {
      safeConsole.error("❌ Failed to initialize Face Detection:", error);
      this.isInitializing = false;
      throw error;
    }
  }

  /**
   * Detect face in video frame
   * @param {HTMLVideoElement} videoElement - Video element to process
   * @param {number} timestamp - Video timestamp in milliseconds
   * @returns {Object|null} Face results or null if no face detected
   */
  async detectFace(videoElement, timestamp) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!videoElement || videoElement.readyState < 2) {
      return null;
    }

    const startTime = performance.now();

    try {
      const results = this.faceLandmarker.detectForVideo(
        videoElement,
        timestamp,
      );

      // Performance tracking
      const detectionTime = performance.now() - startTime;
      this._updatePerformanceMetrics(detectionTime);

      return results.faceLandmarks && results.faceLandmarks.length > 0
        ? results
        : null;
    } catch (error) {
      safeConsole.error("Face detection error:", error);
      return null;
    }
  }

  _updatePerformanceMetrics(detectionTime) {
    this.performanceMetrics.frameCount++;
    this.performanceMetrics.lastDetectionTime = detectionTime;

    // Exponential moving average
    const alpha = 0.1;
    this.performanceMetrics.averageDetectionTime =
      alpha * detectionTime +
      (1 - alpha) * this.performanceMetrics.averageDetectionTime;
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics() {
    return { ...this.performanceMetrics };
  }

  /**
   * Cleanup resources
   */
  dispose() {
    this.faceLandmarker = null;
    this.isInitialized = false;
    this.isInitializing = false;
  }
}

// Singleton instance
export const faceDetectionService = new FaceDetectionService();
